import { useEffect, useMemo, useState } from "react";
import { Drawer, Modal, message } from "antd";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Footer, Logo } from "../../components/care/CareLayout";
import AutoContactStrip from "../../components/care/AutoContactStrip";
import {
  emptyInventoryFilters,
  InventoryFilterPanel,
  inventoryFiltersFromSearchParams,
  syncInventoryFiltersToSearchParams,
} from "../../components/care/InventoryFilterPanel";
import VehicleImage from "../../components/care/VehicleImage";
import Seo, { absoluteUrl } from "../../components/seo/Seo";
import useSiteProfile from "../../hooks/useSiteProfile";
import { careService } from "../../services/careService";
import { VehicleListingDetails, listingDetailsFor } from "./VehicleDetailPage";

const INVENTORY_PAGE_SIZE = 12;
const paginationItems = (currentPage, totalPages) => {
  const pages = new Set([1, totalPages]);
  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) pages.add(page);
  }
  return [...pages]
    .sort((a, b) => a - b)
    .reduce((items, page, index, sortedPages) => {
      if (index && page - sortedPages[index - 1] > 1) items.push("ellipsis");
      items.push(page);
      return items;
    }, []);
};
const scheduledVisitAtFromForm = (form = {}) => {
  if (form.scheduleVisit !== "yes" || !form.visitDate || !form.visitTime) return null;
  const date = new Date(`${form.visitDate}T${form.visitTime}:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};
const firstText = (...values) => values.find((value) => value !== null && value !== undefined && value !== "") || "";
const sentenceText = (value) => {
  const text = String(value || "").trim();
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

const fallbackSiteProfile = {
  name: "Auto Portal",
  themePhrase: "Find your next car",
  phone: "",
  email: "",
  location: "",
  logo: "",
  logoUrl: "",
  favicon: "",
  faviconUrl: "",
};

export function PublicNav({ siteProfile = fallbackSiteProfile } = {}) {
  return (
    <header className="topbar public-topbar">
      <Logo siteProfile={siteProfile} />
      <nav className="public-nav" aria-label="Main navigation">
        <a href="#inventory">Inventory</a>
        <a href="#filters">Search</a>
        <a href="#location">Location</a>
        <a href="#contact">Contact Us</a>
      </nav>
      <div className="topbar-actions">
        <a className="btn primary" href="#inventory"><i className="ti ti-car" /> View Cars</a>
      </div>
    </header>
  );
}

export default function LandingPage() {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [vehiclesError, setVehiclesError] = useState("");
  const [inventoryPage, setInventoryPage] = useState(Number(searchParams.get("page") || 0) || 0);
  const [inventoryPageMeta, setInventoryPageMeta] = useState({
    number: 0,
    size: INVENTORY_PAGE_SIZE,
    totalElements: 0,
    totalPages: 1,
    first: true,
    last: true,
  });
  const [inventoryFilters, setInventoryFilters] = useState(() => inventoryFiltersFromSearchParams(searchParams));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [inventorySearchDraft, setInventorySearchDraft] = useState(() => inventoryFiltersFromSearchParams(searchParams).query || "");
  const [selectedPhotoByVehicleId, setSelectedPhotoByVehicleId] = useState({});
  const [photoGallery, setPhotoGallery] = useState({ vehicle: null, index: 0 });
  const [detailVehicle, setDetailVehicle] = useState(null);
  const [detailVehicleLoading, setDetailVehicleLoading] = useState(false);
  const [detailVehicleError, setDetailVehicleError] = useState("");
  const [inquiryVehicle, setInquiryVehicle] = useState(null);
  const [inquiryForm, setInquiryForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    zipCode: "",
    phone: "",
    scheduleVisit: "no",
    visitDate: "",
    visitTime: "",
    messageToSeller: "",
  });
  const [inquiryError, setInquiryError] = useState("");
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const { siteProfile: fetchedSiteProfile } = useSiteProfile();
  const siteProfile = { ...fallbackSiteProfile, ...fetchedSiteProfile };

  const dealerName = firstText(siteProfile.name, fallbackSiteProfile.name);
  const dealerThemePhrase = firstText(siteProfile.themePhrase, fallbackSiteProfile.themePhrase);
  const dealerPhoneDigits = String(firstText(siteProfile.phone, fallbackSiteProfile.phone)).replace(/\D+/g, "");
  const dealerEmail = firstText(siteProfile.email, fallbackSiteProfile.email);
  const dealerLocation = firstText(siteProfile.location, fallbackSiteProfile.location);
  const dealerLocationLabel = dealerLocation || "your area";
  const heroTitle = dealerName && dealerThemePhrase.toLowerCase().includes(dealerName.toLowerCase())
    ? dealerThemePhrase
    : `${dealerThemePhrase} at ${dealerName}`;
  const logoCandidate = firstText(siteProfile.logoUrl, siteProfile.logoSignedUrl, siteProfile.logo, fallbackSiteProfile.logo);
  const dealerLogo = /^(https?:|\/|data:image|blob:)/i.test(String(logoCandidate || ""))
    ? logoCandidate
    : "";
  const faviconCandidate = firstText(siteProfile.faviconUrl, siteProfile.faviconSignedUrl, siteProfile.favicon);
  const dealerFavicon = /^(https?:|\/|data:image|blob:)/i.test(String(faviconCandidate || ""))
    ? faviconCandidate
    : "";

  const pageItems = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.content)) return value.content;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };
  const pageMeta = (value, items, requestedPage) => {
    const pageSize = Number(value?.size ?? value?.page?.size ?? INVENTORY_PAGE_SIZE) || INVENTORY_PAGE_SIZE;
    const totalElements = Number(value?.totalElements ?? value?.total ?? value?.page?.totalElements ?? items.length) || 0;
    const totalPages = Math.max(1, Number(value?.totalPages ?? value?.page?.totalPages ?? Math.ceil(totalElements / pageSize)) || 1);
    const pageNumber = Number(value?.number ?? value?.pageNumber ?? value?.page?.number ?? requestedPage) || 0;
    return {
      number: pageNumber,
      size: pageSize,
      totalElements,
      totalPages,
      first: value?.first ?? pageNumber <= 0,
      last: value?.last ?? pageNumber >= totalPages - 1,
    };
  };
  const numericValue = (value) => {
    const numeric = Number(String(value || "").replace(/[^\d.]/g, ""));
    return Number.isFinite(numeric) ? numeric : 0;
  };
  const formatPrice = (value) => {
    const text = String(value || "").trim();
    if (!text) return "Price pending";
    if (/^\$/.test(text)) return text;
    const numeric = Number(text.replace(/[^\d.]/g, ""));
    return Number.isFinite(numeric) && numeric > 0
      ? numeric.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
      : text;
  };
  const formatMileage = (value) => {
    const text = String(value || "").trim();
    if (!text) return "Mileage pending";
    const numeric = numericValue(text);
    return numeric > 0 ? `${numeric.toLocaleString("en-US")} miles` : text;
  };
  const labelize = (value) =>
    String(value || "")
      .replace(/[_-]+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const statusToken = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  const displayStatus = (value) => {
    const raw = firstText(value, "Ready");
    return statusToken(raw) === "ready" ? "Available" : labelize(raw);
  };
  const vehicleTitle = (vehicle = {}) =>
    firstText(
      [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" "),
      vehicle.title && vehicle.title !== "Vehicle" ? vehicle.title : "",
      vehicle.name,
      vehicle.vehicleName,
      "Vehicle",
    );
  const createdAtTime = (vehicle = {}) => {
    const date = new Date(firstText(vehicle.createdAt, vehicle.createdDate, vehicle.dateCreated, vehicle.insertedAt, vehicle.updatedAt));
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };
  const sortNewestVehicles = (items = []) =>
    [...items].sort((a, b) => createdAtTime(b) - createdAtTime(a));
  const vehiclePhotoUrl = (value) => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (/^blob:/i.test(trimmed)) return "";
      return /^(https?:|\/|data:image)/i.test(trimmed) ? trimmed : "";
    }
    if (!value || typeof value !== "object") return "";
    return vehiclePhotoUrl(firstText(value.signedUrl, value.url, value.photoUrl, value.imageUrl, value.downloadUrl, value.fileUrl, value.publicUrl));
  };
  const cleanSelectOptionValue = (value) =>
    String(value || "").replace(/\s*[[(][^\])]*[\])]\s*$/g, "").trim();
  const normalizeVehicle = (vehicle = {}, index = 0, { includeAllPhotos = false } = {}) => {
    const photoList = Array.isArray(vehicle.photos) ? vehicle.photos.map(vehiclePhotoUrl) : [];
    const photos = [
      vehiclePhotoUrl(vehicle.mainPhoto),
      ...(includeAllPhotos ? photoList : photoList.slice(0, 1)),
    ].filter(Boolean);
    const title = vehicleTitle(vehicle);
    const drivetrain = cleanSelectOptionValue(firstText(vehicle.drivetrain, vehicle.driveType));
    const fuel = cleanSelectOptionValue(firstText(vehicle.fuelType, vehicle.fuel));
    const transmission = cleanSelectOptionValue(firstText(vehicle.transmission));
    const engine = cleanSelectOptionValue(firstText(vehicle.engine));
    const priceSource = firstText(vehicle.price, vehicle.askingPrice, vehicle.listPrice);
    const mileageSource = firstText(vehicle.mileage, vehicle.odometer);
    const normalizedVehicleId = firstText(vehicle.id, vehicle.vehicleId, vehicle.vin, title, index);
    const selectedPhoto = selectedPhotoByVehicleId[normalizedVehicleId];
    return {
      ...vehicle,
      id: normalizedVehicleId,
      title,
      make: cleanSelectOptionValue(firstText(vehicle.make)),
      model: cleanSelectOptionValue(firstText(vehicle.model)),
      trim: cleanSelectOptionValue(firstText(vehicle.trim)),
      bodyType: cleanSelectOptionValue(firstText(vehicle.bodyType, vehicle.bodyStyle, vehicle.type)),
      year: firstText(vehicle.year),
      yearValue: numericValue(vehicle.year),
      fuel,
      drivetrain,
      engine,
      transmission,
      price: formatPrice(priceSource),
      priceValue: numericValue(priceSource),
      mileage: formatMileage(mileageSource),
      mileageValue: numericValue(mileageSource),
      meta: [engine, transmission, drivetrain].filter(Boolean).join(" - ") || firstText(vehicle.bodyType, fuel, "Details pending"),
      status: displayStatus(firstText(vehicle.status, vehicle.inventoryStatus, "Ready")),
      statusKey: statusToken(firstText(vehicle.status, vehicle.inventoryStatus, "Ready")),
      condition: labelize(firstText(vehicle.condition, vehicle.vehicleCondition, vehicle.saleCondition)),
      exteriorColor: cleanSelectOptionValue(firstText(vehicle.exteriorColor, vehicle.color)) || "Color pending",
      interiorColor: cleanSelectOptionValue(firstText(vehicle.interiorColor)),
      photo: photos.includes(selectedPhoto) ? selectedPhoto : photos[0],
      photos,
    };
  };
  const publicVehicles = useMemo(
    () => vehicles.filter((vehicle) => {
      const status = statusToken(firstText(vehicle.status, vehicle.inventoryStatus));
      return !["sold", "completed"].includes(status);
    }),
    [vehicles],
  );
  const newestInventory = useMemo(
    () => sortNewestVehicles(publicVehicles).map((vehicle, index) => normalizeVehicle(vehicle, index)),
    [publicVehicles, selectedPhotoByVehicleId],
  );
  const uniqueOptions = (items) =>
    [...new Set(items.map((item) => String(item || "").trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const filterOptions = useMemo(() => ({
    makes: uniqueOptions(newestInventory.map((vehicle) => vehicle.make)),
    models: uniqueOptions(newestInventory.map((vehicle) => vehicle.model)),
    trims: uniqueOptions(newestInventory.map((vehicle) => vehicle.trim)),
    bodyTypes: uniqueOptions(newestInventory.map((vehicle) => vehicle.bodyType)),
    conditions: uniqueOptions(newestInventory.map((vehicle) => vehicle.condition)),
    years: uniqueOptions(newestInventory.map((vehicle) => vehicle.year)).reverse(),
    fuels: uniqueOptions(newestInventory.map((vehicle) => vehicle.fuel)),
    drivetrains: uniqueOptions(newestInventory.map((vehicle) => vehicle.drivetrain)),
    engines: uniqueOptions(newestInventory.map((vehicle) => vehicle.engine)),
    exteriorColors: uniqueOptions(newestInventory.map((vehicle) => vehicle.exteriorColor)),
    interiorColors: uniqueOptions(newestInventory.map((vehicle) => vehicle.interiorColor)),
    transmissions: uniqueOptions(newestInventory.map((vehicle) => vehicle.transmission)),
  }), [newestInventory]);
  const maxInventoryPrice = Math.max(0, ...newestInventory.map((vehicle) => vehicle.priceValue));
  const maxInventoryMileage = Math.max(0, ...newestInventory.map((vehicle) => vehicle.mileageValue));
  const activeMinPrice = Number(inventoryFilters.minPrice || 0);
  const activeMaxPrice = Number(inventoryFilters.maxPrice || maxInventoryPrice || 0);
  const activeMinMileage = Number(inventoryFilters.minMileage || 0);
  const activeMaxMileage = Number(inventoryFilters.maxMileage || maxInventoryMileage || 0);
  const hasActiveFilters = Object.values(inventoryFilters).some(Boolean);
  const filteredInventory = useMemo(() => {
    const needle = inventoryFilters.query.trim().toLowerCase();
    return newestInventory.filter((vehicle) => {
      const searchText = [
        vehicle.title,
        vehicle.make,
        vehicle.model,
        vehicle.trim,
        vehicle.bodyType,
        vehicle.year,
        vehicle.fuel,
        vehicle.drivetrain,
        vehicle.engine,
        vehicle.transmission,
        vehicle.exteriorColor,
        vehicle.interiorColor,
        vehicle.status,
        vehicle.condition,
        vehicle.meta,
      ].filter(Boolean).join(" ").toLowerCase();
      const statusFilter = cleanSelectOptionValue(inventoryFilters.status);
      const statusFilterToken = statusToken(statusFilter);
      const statusMatches = !statusFilter || ["search_inventory", "newest_cars"].includes(statusFilterToken) || [vehicle.status, vehicle.condition]
        .map(statusToken)
        .includes(statusFilterToken);
      return (
        (!needle || searchText.includes(needle)) &&
        statusMatches &&
        (!inventoryFilters.make || vehicle.make === cleanSelectOptionValue(inventoryFilters.make)) &&
        (!inventoryFilters.model || vehicle.model === cleanSelectOptionValue(inventoryFilters.model)) &&
        (!inventoryFilters.trim || vehicle.trim === cleanSelectOptionValue(inventoryFilters.trim)) &&
        (!inventoryFilters.bodyType || vehicle.bodyType === cleanSelectOptionValue(inventoryFilters.bodyType)) &&
        (!inventoryFilters.condition || vehicle.condition === cleanSelectOptionValue(inventoryFilters.condition)) &&
        (!inventoryFilters.minYear || vehicle.yearValue >= Number(inventoryFilters.minYear)) &&
        (!inventoryFilters.maxYear || vehicle.yearValue <= Number(inventoryFilters.maxYear)) &&
        (!inventoryFilters.fuel || vehicle.fuel === cleanSelectOptionValue(inventoryFilters.fuel)) &&
        (!inventoryFilters.drivetrain || vehicle.drivetrain === cleanSelectOptionValue(inventoryFilters.drivetrain)) &&
        (!inventoryFilters.engine || vehicle.engine === cleanSelectOptionValue(inventoryFilters.engine)) &&
        (!inventoryFilters.exteriorColor || vehicle.exteriorColor === cleanSelectOptionValue(inventoryFilters.exteriorColor)) &&
        (!inventoryFilters.interiorColor || vehicle.interiorColor === cleanSelectOptionValue(inventoryFilters.interiorColor)) &&
        (!inventoryFilters.transmission || vehicle.transmission === cleanSelectOptionValue(inventoryFilters.transmission)) &&
        (!inventoryFilters.minPrice || vehicle.priceValue >= activeMinPrice) &&
        (!inventoryFilters.maxPrice || vehicle.priceValue <= activeMaxPrice) &&
        (!inventoryFilters.minMileage || vehicle.mileageValue >= activeMinMileage) &&
        (!inventoryFilters.maxMileage || vehicle.mileageValue <= activeMaxMileage)
      );
    });
  }, [activeMaxMileage, activeMaxPrice, activeMinMileage, activeMinPrice, inventoryFilters, newestInventory]);
  const landingStructuredData = useMemo(() => {
    const dealer = {
      "@context": "https://schema.org",
      "@type": "AutoDealer",
      "@id": absoluteUrl("/#dealership"),
      name: dealerName,
      url: absoluteUrl("/"),
      logo: dealerLogo ? absoluteUrl(dealerLogo) : undefined,
      image: dealerLogo ? absoluteUrl(dealerLogo) : undefined,
      telephone: dealerPhoneDigits ? `+1${dealerPhoneDigits}` : undefined,
      email: dealerEmail || undefined,
      address: dealerLocation ? {
        "@type": "PostalAddress",
        streetAddress: dealerLocation,
        addressCountry: "US",
      } : undefined,
    };
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${dealerName} inventory`,
      itemListElement: newestInventory.slice(0, 12).map((vehicle, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: absoluteUrl(`/inventory/${vehicle.id}`),
        item: {
          "@type": "Vehicle",
          name: vehicle.title,
          image: vehicle.photo ? absoluteUrl(vehicle.photo) : undefined,
          brand: vehicle.make || undefined,
          model: vehicle.model || undefined,
          vehicleModelDate: vehicle.year || undefined,
          mileageFromOdometer: vehicle.mileageValue ? {
            "@type": "QuantitativeValue",
            value: vehicle.mileageValue,
            unitCode: "SMI",
          } : undefined,
          offers: vehicle.priceValue ? {
            "@type": "Offer",
            price: vehicle.priceValue,
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: absoluteUrl(`/inventory/${vehicle.id}`),
            seller: { "@id": absoluteUrl("/#dealership") },
          } : undefined,
        },
      })),
    };
    return [dealer, itemList];
  }, [dealerEmail, dealerLocation, dealerLogo, dealerName, dealerPhoneDigits, newestInventory]);
  const inventoryTotal = inventoryPageMeta.totalElements || newestInventory.length;
  const inventoryTotalPages = Math.max(1, inventoryPageMeta.totalPages || 1);
  const inventoryCurrentPage = Math.min(inventoryPage + 1, inventoryTotalPages);
  const inventoryStart = inventoryTotal ? (inventoryPage * inventoryPageMeta.size) + 1 : 0;
  const inventoryEnd = inventoryTotal ? Math.min(inventoryTotal, (inventoryPage * inventoryPageMeta.size) + newestInventory.length) : 0;
  const inventoryPageItems = paginationItems(inventoryCurrentPage, inventoryTotalPages);
  const commitInventoryFilters = (nextFilters) => {
    setInventoryFilters(nextFilters);
    setInventoryPage(0);
    setSearchParams(syncInventoryFiltersToSearchParams(searchParams, nextFilters, { deleteKeys: ["page"] }), { replace: true });
  };
  const updateInventoryFilter = (field, value) => {
    const nextFilters = {
      ...inventoryFilters,
      [field]: value,
      ...(field === "make" ? { model: "" } : {}),
    };
    commitInventoryFilters(nextFilters);
  };
  const resetInventoryFilters = () => {
    commitInventoryFilters({ ...emptyInventoryFilters });
  };
  const submitInventorySearch = () => {
    updateInventoryFilter("query", inventorySearchDraft.trim());
  };
  const goToInventoryPage = (page) => {
    const nextPage = Math.max(0, page);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextPage) {
      nextSearchParams.set("page", String(nextPage));
    } else {
      nextSearchParams.delete("page");
    }
    setInventoryPage(nextPage);
    setSearchParams(nextSearchParams, { replace: true });
  };
  const resetInquiryForm = () => {
    setInquiryForm({
      firstName: "",
      lastName: "",
      email: "",
      zipCode: "",
      phone: "",
      scheduleVisit: "no",
      visitDate: "",
      visitTime: "",
      messageToSeller: "",
    });
    setInquiryError("");
  };
  const openVehicleDetails = async (vehicle) => {
    setPhotoGallery({ vehicle: null, index: 0 });
    setDetailVehicle(vehicle);
    setDetailVehicleLoading(true);
    setDetailVehicleError("");
    try {
      const fullVehicle = await careService.vehicles.getPublic(vehicle.id);
      setDetailVehicle(normalizeVehicle(fullVehicle || vehicle, 0, { includeAllPhotos: true }));
    } catch (error) {
      setDetailVehicleError(error?.response?.data?.message || error?.message || "Unable to load all vehicle photos.");
    } finally {
      setDetailVehicleLoading(false);
    }
  };
  const openInquiryForm = (vehicle) => {
    setInquiryVehicle(vehicle);
    resetInquiryForm();
  };
  const closeInquiryForm = () => {
    if (inquirySubmitting) return;
    setInquiryVehicle(null);
    resetInquiryForm();
  };
  const updateInquiryField = (field, value) => {
    setInquiryForm((current) => ({ ...current, [field]: value }));
  };
  const submitVehicleInquiry = async (event) => {
    event.preventDefault();
    if (!inquiryVehicle?.id) return;
    const payload = {
      firstName: inquiryForm.firstName.trim(),
      lastName: inquiryForm.lastName.trim(),
      email: inquiryForm.email.trim(),
      zipCode: inquiryForm.zipCode.trim(),
      phone: inquiryForm.phone.trim(),
      scheduledVisitAt: scheduledVisitAtFromForm(inquiryForm),
      messageToSeller: inquiryForm.messageToSeller.trim(),
      vehicleId: inquiryVehicle.id,
    };
    if (!payload.firstName || !payload.lastName || !payload.email || !payload.zipCode) {
      setInquiryError("First name, last name, email, and ZIP code are required.");
      return;
    }
    if (inquiryForm.scheduleVisit === "yes" && !payload.scheduledVisitAt) {
      setInquiryError("Choose a visit date and time.");
      return;
    }
    setInquirySubmitting(true);
    setInquiryError("");
    try {
      await careService.vehicles.createInquiryPublic(inquiryVehicle.id, payload);
      message.success("Thanks. Your vehicle inquiry was sent.");
      setInquiryVehicle(null);
      resetInquiryForm();
    } catch (error) {
      setInquiryError(error?.response?.data?.message || error?.message || "Unable to send your inquiry right now.");
    } finally {
      setInquirySubmitting(false);
    }
  };
  const closePhotoGallery = () => setPhotoGallery({ vehicle: null, index: 0 });
  const selectGalleryPhoto = (vehicle, index) => {
    const photo = vehicle.photos[index];
    if (!photo) return;
    setPhotoGallery({ vehicle, index });
    setSelectedPhotoByVehicleId((current) => ({ ...current, [vehicle.id]: photo }));
  };
  const shiftGalleryPhoto = (direction) => {
    setPhotoGallery((current) => {
      const photos = current.vehicle?.photos || [];
      if (!photos.length) return current;
      const index = (current.index + direction + photos.length) % photos.length;
      setSelectedPhotoByVehicleId((selected) => ({ ...selected, [current.vehicle.id]: photos[index] }));
      return { ...current, index };
    });
  };
  const scrollToPageTop = () => {
    const scrollOptions = { top: 0, left: 0, behavior: "smooth" };
    [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.getElementById("root"),
      document.querySelector(".auto-landing-page"),
    ].filter(Boolean).forEach((element) => {
      if (typeof element.scrollTo === "function") {
        element.scrollTo(scrollOptions);
      } else {
        element.scrollTop = 0;
      }
    });
    window.scrollTo(scrollOptions);
    document.getElementById("page-top")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const nextFilters = inventoryFiltersFromSearchParams(searchParams);
    setInventoryFilters((current) => (
      JSON.stringify(current) === JSON.stringify(nextFilters) ? current : nextFilters
    ));
    setInventorySearchDraft((current) => (current === (nextFilters.query || "") ? current : nextFilters.query || ""));
    const nextPage = Number(searchParams.get("page") || 0) || 0;
    setInventoryPage((current) => (current === nextPage ? current : nextPage));
  }, [searchParams]);

  useEffect(() => {
    let isMounted = true;
    setVehiclesLoading(true);
    setVehiclesError("");
    careService.vehicles.listPublicPage({ page: inventoryPage, size: INVENTORY_PAGE_SIZE, sort: "createdAt,desc" })
      .then((data) => {
        if (!isMounted) return;
        const nextVehicles = sortNewestVehicles(pageItems(data));
        setVehicles(nextVehicles);
        setInventoryPageMeta(pageMeta(data, nextVehicles, inventoryPage));
        setSelectedPhotoByVehicleId((current) => {
          const nextSelected = {};
          nextVehicles.forEach((vehicle, index) => {
            const title = vehicleTitle(vehicle);
            const vehicleId = firstText(vehicle.id, vehicle.vehicleId, vehicle.vin, title, index);
            const photo = vehiclePhotoUrl(vehicle.mainPhoto) || (Array.isArray(vehicle.photos) ? vehicle.photos.map(vehiclePhotoUrl).find(Boolean) : "");
            nextSelected[vehicleId] = current[vehicleId] && current[vehicleId] === photo
              ? current[vehicleId]
              : photo;
          });
          return nextSelected;
        });
      })
      .catch((error) => {
        if (!isMounted) return;
        setVehicles([]);
        setInventoryPageMeta({
          number: inventoryPage,
          size: INVENTORY_PAGE_SIZE,
          totalElements: 0,
          totalPages: 1,
          first: true,
          last: true,
        });
        setVehiclesError(error?.response?.data?.message || error?.message || "Unable to load live inventory.");
      })
      .finally(() => {
        if (isMounted) setVehiclesLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [inventoryPage]);

  if (!loading && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="page public-page auto-landing-page" id="page-top">
      <Seo
        title={`${dealerName} | Used Cars in ${dealerLocationLabel}`}
        description={`Search ${dealerName} inventory in ${dealerLocationLabel}. Browse available vehicles by make, model, price, mileage, color, drivetrain, and features.`}
        canonicalPath="/"
        favicon={dealerFavicon}
        image={dealerLogo}
        structuredData={landingStructuredData}
      />
      <AutoContactStrip siteProfile={siteProfile} />
      <PublicNav siteProfile={siteProfile} />
      <main>
        <section className="hero auto-storefront-hero">
          <div className="hero-copy">
            <span className="auto-dealer-kicker">WELCOME TO {dealerName}</span>
            <h1 className="auto-landing-hero-title">{sentenceText(heroTitle)}</h1>
            <div className="button-row">
              <a className="btn primary" href="#filters"><i className="ti ti-search" /> Search Inventory</a>
              <a className="btn secondary" href="#inventory"><i className="ti ti-list-details" /> Newest Cars</a>
              <a className="btn secondary auto-upcoming-button" href="/?status=Upcoming#inventory"><i className="ti ti-clock-up" /> Upcoming</a>
            </div>
            <form
              className="auto-hero-search auto-public-search"
              onSubmit={(event) => {
                event.preventDefault();
                submitInventorySearch();
              }}
            >
              <i className="ti ti-search" />
              <input
                aria-label="Search inventory"
                placeholder="Search make, model, trim, color, body style, or feature"
                value={inventorySearchDraft}
                onChange={(event) => setInventorySearchDraft(event.target.value)}
              />
              <button type="submit" aria-label="Search inventory">
                <i className="ti ti-search" />
              </button>
            </form>
          </div>
        </section>

        <section className="public-section auto-dealer-section" id="inventory">
          <button
            className="auto-mobile-filter-toggle"
            type="button"
            onClick={() => setMobileFiltersOpen((isOpen) => !isOpen)}
            aria-controls="filters"
            aria-expanded={mobileFiltersOpen}
          >
            <i className="ti ti-adjustments-horizontal" />
            {mobileFiltersOpen ? "Hide filters" : "Filter"}
          </button>
          <div className={`auto-inventory-layout${mobileFiltersOpen ? " filters-open" : ""}`} id="filters">
            <InventoryFilterPanel
              activeMinMileage={activeMinMileage}
              activeMaxMileage={activeMaxMileage}
              activeMinPrice={activeMinPrice}
              activeMaxPrice={activeMaxPrice}
              filterOptions={filterOptions}
              filters={inventoryFilters}
              hasActiveFilters={hasActiveFilters}
              maxMileage={maxInventoryMileage}
              maxPrice={maxInventoryPrice}
              matchCount={filteredInventory.length}
              onReset={resetInventoryFilters}
              onUpdateFilter={updateInventoryFilter}
              priceLabel={formatPrice}
            />
            <div className="auto-inventory-grid">
              {vehiclesLoading ? (
                <p className="auto-inventory-empty">Loading inventory...</p>
              ) : null}
              {!vehiclesLoading && !vehiclesError && !filteredInventory.length ? (
                <p className="auto-inventory-empty">No vehicles match these filters.</p>
              ) : null}
              {!vehiclesLoading && filteredInventory.map((vehicle) => (
                <article className="auto-inventory-card" key={vehicle.id || vehicle.title}>
                  <div className="auto-car-visual">
                    <button
                      className="auto-car-visual-button"
                      onClick={() => openVehicleDetails(vehicle)}
                      type="button"
                      aria-label={`Open ${vehicle.title} details`}
                    >
                      <VehicleImage src={vehicle.photo} alt="" />
                    </button>
                  </div>
                  <div className="auto-inventory-card-body">
                    <span className={`auto-inventory-status is-${vehicle.statusKey || "ready"}`}>{vehicle.status}</span>
                    <button className="auto-inventory-card-details" type="button" onClick={() => openVehicleDetails(vehicle)} aria-label={`View ${vehicle.title} details`}>
                      <h3>{vehicle.title}</h3>
                      <strong>{vehicle.price}</strong>
                      <p>{vehicle.mileage} - {vehicle.exteriorColor}</p>
                      <small>{vehicle.meta}</small>
                    </button>
                    <div className="auto-card-icon-actions">
                      <button className="auto-card-like-button" type="button" aria-label={`Like ${vehicle.title}`}>
                        <i className="ti ti-heart" />
                      </button>
                      <button className="auto-card-detail-link" type="button" onClick={() => openVehicleDetails(vehicle)} aria-label={`View ${vehicle.title} details`}>
                        <i className="ti ti-external-link" />
                      </button>
                      <a className="auto-card-call-icon" href="tel:2533254800" aria-label={`Call about ${vehicle.title}`}>
                        <i className="ti ti-phone-call" />
                      </a>
                      <button className="auto-card-contact-icon" type="button" onClick={() => openInquiryForm(vehicle)} aria-label={`Contact seller about ${vehicle.title}`}>
                        <i className="ti ti-message-circle" />
                      </button>
                    </div>
                    <div className="auto-card-cta-row">
                      <button className="auto-card-availability-link" type="button" onClick={() => openInquiryForm(vehicle)}>
                        <i className="ti ti-circle-check" />
                        Check Availability
                      </button>
                      <a className="auto-card-call-link" href="tel:2533254800" aria-label={`Call about ${vehicle.title}`}>
                        <i className="ti ti-phone-call" />
                        Call
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
          {!vehiclesError && (inventoryTotal > INVENTORY_PAGE_SIZE || inventoryTotalPages > 1) ? (
            <div className="timesheet-pagination auto-inventory-pagination">
              <span>
                Showing <strong>{inventoryStart}-{inventoryEnd}</strong> of <strong>{inventoryTotal}</strong>
              </span>
              <div className="timesheet-pagination-actions">
                <button
                  className="mini-btn"
                  type="button"
                  onClick={() => goToInventoryPage(inventoryPage - 1)}
                  disabled={vehiclesLoading || inventoryCurrentPage <= 1}
                >
                  <i className="ti ti-chevron-left" /> Previous
                </button>
                <div className="inventory-page-number-list" aria-label="Inventory pages">
                  {inventoryPageItems.map((page, index) => (
                    page === "ellipsis" ? (
                      <span className="inventory-page-ellipsis" key={`ellipsis-${index}`}>...</span>
                    ) : (
                      <button
                        aria-current={page === inventoryCurrentPage ? "page" : undefined}
                        aria-label={`Go to page ${page}`}
                        className={`inventory-page-number${page === inventoryCurrentPage ? " is-active" : ""}`}
                        disabled={vehiclesLoading || page === inventoryCurrentPage}
                        key={page}
                        onClick={() => goToInventoryPage(page - 1)}
                        type="button"
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                <button
                  className="mini-btn"
                  type="button"
                  onClick={() => goToInventoryPage(Math.min(inventoryTotalPages - 1, inventoryPage + 1))}
                  disabled={vehiclesLoading || inventoryCurrentPage >= inventoryTotalPages}
                >
                  Next <i className="ti ti-chevron-right" />
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <Modal
          centered
          className="auto-inquiry-modal"
          footer={null}
          onCancel={closeInquiryForm}
          open={Boolean(inquiryVehicle)}
          title={inquiryVehicle ? `I'm interested in this ${inquiryVehicle.title}` : "Vehicle inquiry"}
          width={660}
        >
          {inquiryVehicle ? (
            <form className="auto-inquiry-form" onSubmit={submitVehicleInquiry}>
              <div className="auto-inquiry-vehicle-card">
                <div className="auto-inquiry-vehicle-photo">
                  <VehicleImage src={inquiryVehicle.photo} alt="" />
                </div>
                <div>
                  <strong>{inquiryVehicle.title}</strong>
                  <span>{inquiryVehicle.status}</span>
                </div>
                <b>{inquiryVehicle.price}</b>
              </div>
              <p className="auto-inquiry-copy">Confirm availability and pricing for this vehicle.</p>
              <div className="auto-inquiry-grid">
                <label>
                  <span>First name</span>
                  <input
                    autoComplete="given-name"
                    onChange={(event) => updateInquiryField("firstName", event.target.value)}
                    placeholder="First name"
                    required
                    value={inquiryForm.firstName}
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input
                    autoComplete="family-name"
                    onChange={(event) => updateInquiryField("lastName", event.target.value)}
                    placeholder="Last name"
                    required
                    value={inquiryForm.lastName}
                  />
                </label>
                <label className="auto-inquiry-wide">
                  <span>Email address</span>
                  <input
                    autoComplete="email"
                    onChange={(event) => updateInquiryField("email", event.target.value)}
                    placeholder="Email address"
                    required
                    type="email"
                    value={inquiryForm.email}
                  />
                </label>
                <label>
                  <span>ZIP code</span>
                  <input
                    autoComplete="postal-code"
                    inputMode="numeric"
                    onChange={(event) => updateInquiryField("zipCode", event.target.value)}
                    placeholder="98012"
                    required
                    value={inquiryForm.zipCode}
                  />
                </label>
                <label>
                  <span>Phone (optional)</span>
                  <input
                    autoComplete="tel"
                    onChange={(event) => updateInquiryField("phone", event.target.value)}
                    placeholder="+1 555-555-5555"
                    type="tel"
                    value={inquiryForm.phone}
                  />
                </label>
                <div className="auto-inquiry-wide auto-inquiry-schedule">
                  <div>
                    <strong>Schedule a visit?</strong>
                  </div>
                  <div className="auto-inquiry-schedule-options" role="group" aria-label="Schedule a visit?">
                    <button
                      className={inquiryForm.scheduleVisit === "yes" ? "active" : ""}
                      type="button"
                      onClick={() => updateInquiryField("scheduleVisit", "yes")}
                    >
                      <i className="ti ti-circle-check" /> Yes
                    </button>
                    <button
                      className={inquiryForm.scheduleVisit === "no" ? "active" : ""}
                      type="button"
                      onClick={() => setInquiryForm((current) => ({ ...current, scheduleVisit: "no", visitDate: "", visitTime: "" }))}
                    >
                      <i className="ti ti-circle-x" /> No
                    </button>
                  </div>
                </div>
                {inquiryForm.scheduleVisit === "yes" ? (
                  <>
                    <p className="auto-inquiry-wide auto-inquiry-schedule-help">Pick a date and time if you want the seller to plan around your visit.</p>
                    <label>
                      <span>Visit date</span>
                      <input
                        onChange={(event) => updateInquiryField("visitDate", event.target.value)}
                        required={inquiryForm.scheduleVisit === "yes"}
                        type="date"
                        value={inquiryForm.visitDate}
                      />
                    </label>
                    <label>
                      <span>Visit time</span>
                      <input
                        onChange={(event) => updateInquiryField("visitTime", event.target.value)}
                        required={inquiryForm.scheduleVisit === "yes"}
                        step="60"
                        type="time"
                        value={inquiryForm.visitTime}
                      />
                    </label>
                  </>
                ) : null}
                <label className="auto-inquiry-wide">
                  <span>Message to seller (optional)</span>
                  <textarea
                    onChange={(event) => updateInquiryField("messageToSeller", event.target.value)}
                    placeholder="Add any questions or details for the seller"
                    value={inquiryForm.messageToSeller}
                  />
                </label>
              </div>
              {inquiryError ? <p className="auto-inquiry-error">{inquiryError}</p> : null}
              <p className="auto-inquiry-disclaimer">
                By clicking Check availability, you agree to send this information to {dealerName} so they can respond about this vehicle.
              </p>
              <div className="auto-inquiry-actions">
                <button className="btn primary" disabled={inquirySubmitting} type="submit">
                  <i className="ti ti-send" /> {inquirySubmitting ? "Sending..." : "Check availability"}
                </button>
              </div>
            </form>
          ) : null}
        </Modal>

        <Modal
          centered
          className="auto-photo-gallery-modal"
          footer={null}
          onCancel={closePhotoGallery}
          open={Boolean(photoGallery.vehicle)}
          title={photoGallery.vehicle ? `${photoGallery.vehicle.title} photos` : "Vehicle photos"}
          width={980}
        >
          {photoGallery.vehicle ? (
            <div className="auto-photo-gallery">
              <div className="auto-photo-gallery-actions">
                <button className="auto-photo-gallery-detail-link" type="button" onClick={() => openVehicleDetails(photoGallery.vehicle)}>
                  <i className="ti ti-external-link" /> View Details
                </button>
              </div>
              <div className="auto-photo-gallery-stage">
                {photoGallery.vehicle.photos.length > 1 ? (
                  <button className="auto-photo-gallery-nav is-prev" type="button" onClick={() => shiftGalleryPhoto(-1)} aria-label="Previous photo">
                    <i className="ti ti-chevron-left" />
                  </button>
                ) : null}
                <VehicleImage src={photoGallery.vehicle.photos[photoGallery.index]} alt={`${photoGallery.vehicle.title} view ${photoGallery.index + 1}`} />
                {photoGallery.vehicle.photos.length > 1 ? (
                  <button className="auto-photo-gallery-nav is-next" type="button" onClick={() => shiftGalleryPhoto(1)} aria-label="Next photo">
                    <i className="ti ti-chevron-right" />
                  </button>
                ) : null}
              </div>
              {photoGallery.vehicle.photos.length > 1 ? (
                <div className="auto-photo-gallery-thumbs">
                  {photoGallery.vehicle.photos.map((photo, index) => (
                    <button
                      className={index === photoGallery.index ? "is-active" : ""}
                      key={`${photo}-${index}`}
                      onClick={() => selectGalleryPhoto(photoGallery.vehicle, index)}
                      type="button"
                      aria-label={`Show photo ${index + 1}`}
                    >
                      <VehicleImage src={photo} alt={`${photoGallery.vehicle.title} thumbnail ${index + 1}`} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal>

        <Drawer
          className="auto-vehicle-detail-drawer"
          onClose={() => {
            setDetailVehicle(null);
            setDetailVehicleLoading(false);
            setDetailVehicleError("");
          }}
          open={Boolean(detailVehicle)}
          placement="right"
          title={detailVehicle ? detailVehicle.title : "Vehicle details"}
          width="min(920px, 96vw)"
        >
          {detailVehicle ? (
            <div className="auto-vehicle-detail-drawer__content">
              {detailVehicleLoading ? (
                <div className="auto-vehicle-detail-loading page-inline-loader" role="status" aria-live="polite" aria-label="Loading vehicle details">
                  <i className="ti ti-loader-2" aria-hidden="true" />
                </div>
              ) : null}
              <div className="auto-vehicle-detail-drawer__lead">
                <div>
                  <span className={`auto-inventory-status is-${detailVehicle.statusKey || "ready"}`}>{detailVehicle.status || "Available"}</span>
                  <h2>{detailVehicle.title}</h2>
                  <p>{[detailVehicle.condition, detailVehicle.exteriorColor, detailVehicle.meta].filter(Boolean).join(" - ")}</p>
                </div>
                <strong>{detailVehicle.price || "Price pending"}</strong>
              </div>
              <div className="auto-vehicle-detail-drawer__key-points" aria-label={`${detailVehicle.title} highlights`}>
                {[
                  ["ti-gauge", "Mileage", detailVehicle.mileage],
                  ["ti-palette", "Exterior", detailVehicle.exteriorColor],
                  ["ti-steering-wheel", "Drivetrain", detailVehicle.drivetrain],
                ].map(([icon, label, value]) => (
                  <div key={label}>
                    <i className={`ti ${icon}`} />
                    <span>{label}</span>
                    <strong>{value || "Not listed"}</strong>
                  </div>
                ))}
              </div>
              <div className="auto-vehicle-detail-drawer__actions">
                <button className="btn primary" type="button" onClick={() => openInquiryForm(detailVehicle)}>
                  <i className="ti ti-message-circle" /> Confirm availability
                </button>
                {dealerPhoneDigits ? (
                  <a className="btn secondary" href={`tel:${dealerPhoneDigits}`} aria-label={`Call about ${detailVehicle.title}`}>
                    <i className="ti ti-phone-call" /> Call dealer
                  </a>
                ) : null}
                <Link className="btn secondary" to={`/inventory/${detailVehicle.id}`}>
                  <i className="ti ti-external-link" /> View full listing
                </Link>
              </div>
              <VehicleListingDetails
                details={detailVehicle.listingDetails || listingDetailsFor(detailVehicle)}
                onInquiry={() => openInquiryForm(detailVehicle)}
                hideFeaturesSection
                publicMode
                vehicle={{
                  ...detailVehicle,
                  mainPhoto: detailVehicle.photo || detailVehicle.mainPhoto,
                  name: detailVehicle.title,
                  vehicleName: detailVehicle.title,
                }}
              />
              {detailVehicleError ? (
                <p className="form-error">{detailVehicleError}</p>
              ) : null}
            </div>
          ) : null}
        </Drawer>

        <section className="public-section auto-dealer-section" id="location">
          <h2>Location</h2>
          <div className="auto-info-panel">
            <div className="auto-info-card">
              <i className="ti ti-map-pin" />
              <div>
                <span>Visit us</span>
                <strong>{dealerLocation || "Location not listed"}</strong>
                <p>Stop by the lot to view current inventory and talk through your next vehicle.</p>
              </div>
            </div>
            {dealerLocation ? (
              <a
                className="btn secondary"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dealerLocation)}`}
                target="_blank"
                rel="noreferrer"
              >
                <i className="ti ti-map-2" /> Open Map
              </a>
            ) : null}
          </div>
        </section>

        <section className="public-section auto-dealer-section" id="contact">
          <h2>Contact Us</h2>
          <div className="auto-info-panel">
            <div className="auto-info-card">
              <i className="ti ti-phone" />
              <div>
                <span>Call {dealerName}</span>
                <strong>{firstText(siteProfile.phone, "Phone not listed")}</strong>
                <p>Questions about pricing, availability, buying, selling, or trades? Give us a call.</p>
              </div>
            </div>
            <div className="auto-info-actions">
              {dealerPhoneDigits ? (
                <>
                  <a className="btn primary" href={`tel:${dealerPhoneDigits}`}><i className="ti ti-phone-call" /> Call Now</a>
                  <a
                    className="btn secondary"
                    href={`https://wa.me/1${dealerPhoneDigits}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="ti ti-brand-whatsapp" /> WhatsApp
                  </a>
                </>
              ) : null}
            </div>
          </div>
        </section>
      </main>
      <Footer publicLinks siteProfile={siteProfile} />
      <button
        className="auto-back-to-top"
        type="button"
        onClick={scrollToPageTop}
        aria-label="Back to top"
      >
        <i className="ti ti-arrow-up" />
        <span>Back to top</span>
      </button>
    </div>
  );
}
